import React, { useEffect } from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import Radio from '@material-ui/core/Radio';
import ListItemText from '@material-ui/core/ListItemText';
import IconButton from '@material-ui/core/IconButton';
import CircularProgress from '@material-ui/core/CircularProgress';
import makeStyles from '@material-ui/core/styles/makeStyles';
import DeleteIcon from '@material-ui/icons/Delete';

const useStyles = makeStyles((theme) => ({
  header: {
    fontSize: '16px',
    fontWeight: 700,
  },
  container: {
    padding: theme.spacing(0, 3),
  },
  list: {
    width: '100%',
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(1, 0),
    boxSizing: 'border-box',
  },
  listItem: {
    borderBottom: '1px solid #efefef',
    paddingLeft: '0',
  },
  listItemIcon: {
    minWidth: '0px',
    marginRight: '9px',
  },
  listItemIconNew: {
    minWidth: '0px',
    marginRight: '15px',
  },
  radio: {
    padding: '0px',
  },
  listItemText: {
    paddingLeft: '20px',
  },
}));

interface BankAccount {
  paymentMethodId: string;
  bankName: string;
  financialConnectionsAccount: string;
  fingerprint: string;
  lastDigits: string;
  routingNumber: string;
  failedLastTime: boolean;
}

interface Props {
  bankAccounts?: BankAccount[];
  pickedAccount: string;
  setPickedAccount: (value: string) => void;
  removeAccount: (id: string) => void;
  removing: string;
  defaultAccount: string;
}

const NEW_ACCOUNT = 'NEW_ACCOUNT';

const SavedBankAccounts = ({
  bankAccounts,
  pickedAccount,
  setPickedAccount,
  removeAccount,
  removing,
  defaultAccount,
}: Props) => {
  const classes = useStyles();

  useEffect(() => {
    setPickedAccount(defaultAccount);
  }, [defaultAccount]); //eslint-disable-line

  useEffect(() => {
    if (
      pickedAccount !== NEW_ACCOUNT &&
      !bankAccounts?.some((account) => account.paymentMethodId === pickedAccount)
    ) {
      setPickedAccount(defaultAccount);
    }
  }, [bankAccounts, defaultAccount, pickedAccount, setPickedAccount]);

  if (!bankAccounts?.length) {
    return null;
  }

  const formatAccountNumber = (value) => {
    return '●●●● ●●●● ●●●● ' + value;
  };

  const handleToggle = (value) => () => {
    setPickedAccount(value);
  };

  const handleRemoveAccount = (id: string, event) => {
    event.stopPropagation();
    removeAccount(id);
  };

  return (
    <Box className={classes.container}>
      <Typography className={classes.header}>Saved bank accounts</Typography>
      <List className={classes.list}>
        {bankAccounts?.map((account) => (
          <ListItem
            className={classes.listItem}
            key={account.paymentMethodId}
            role={undefined}
            dense
            button
            onClick={handleToggle(account.paymentMethodId)}>
            <ListItemIcon className={classes.listItemIcon}>
              <Radio
                className={classes.radio}
                color="primary"
                checked={pickedAccount === account.paymentMethodId}
                value={account.paymentMethodId}
              />
            </ListItemIcon>

            <ListItemText
              className={classes.listItemText}
              primary={formatAccountNumber(account.lastDigits)}
            />

            <IconButton
              edge="end"
              onClick={(event) => handleRemoveAccount(account.paymentMethodId, event)}>
              {removing === account.paymentMethodId ? <CircularProgress /> : <DeleteIcon />}
            </IconButton>
          </ListItem>
        ))}

        <ListItem
          className={classes.listItem}
          key={NEW_ACCOUNT}
          role={undefined}
          dense
          button
          onClick={handleToggle(NEW_ACCOUNT)}>
          <ListItemIcon className={classes.listItemIconNew}>
            <Radio
              className={classes.radio}
              color="primary"
              checked={pickedAccount === NEW_ACCOUNT}
              value={NEW_ACCOUNT}
            />
          </ListItemIcon>
          <Typography component="div">Use new account</Typography>
        </ListItem>
      </List>
    </Box>
  );
};

export default SavedBankAccounts;
